import {
	type NodeViewComponentProps,
	useEditorEventCallback,
	useEditorState,
	useIsNodeSelected,
	useStopEvent,
} from "@handlewithcare/react-prosemirror";
import { HoverCardPortal } from "@radix-ui/react-hover-card";
import cx from "classnames";
import Image from "next/image";
import { NodeSelection } from "prosemirror-state";
import { forwardRef, useEffect, useState } from "react";

import {
	GitMergeIcon,
	PullRequestClosedIcon,
	PullRequestDraftIcon,
	PullRequestIcon,
} from "@moment/icons";
import {
	HoverCard,
	HoverCardContent,
	HoverCardTrigger,
	LinkButton,
	Separator,
	cn,
	inlineLinkChipClassName,
} from "@moment/ui";

import { usePullRequestData } from "./usePullRequestData";

// Determine colors based on PR state
const getStateColors = (state: "open" | "closed" | "merged" | unknown) => {
	switch (state) {
		case "open":
			return {
				iconColor: "text-palette-positive-9 [&_svg]:text-fixed-light-default",
				bgColor: "bg-palette-positive-9",
				Icon: PullRequestIcon,
			};
		case "closed":
			return {
				iconColor: "text-palette-negative-9 [&_svg]:text-fixed-light-default",
				bgColor: "bg-palette-negative-9",
				Icon: PullRequestClosedIcon,
			};
		case "merged":
			return {
				iconColor: "text-palette-brand-9 [&_svg]:text-fixed-light-default",
				bgColor: "bg-palette-brand-9",
				Icon: GitMergeIcon,
			};
		default:
			return {
				iconColor: "text-palette-gray-9 [&_svg]:text-palette-gray-12",
				bgColor: "bg-palette-gray-7",
				Icon: PullRequestDraftIcon,
			};
	}
};

export const LinkPullRequest = forwardRef<HTMLSpanElement, NodeViewComponentProps>(
	function LinkPullRequest({ nodeProps, children: _, ...props }, ref) {
		const { node, getPos } = nodeProps;

		const url = node.attrs["url"] as string;
		const owner = node.attrs["owner"] as string;
		const repo = node.attrs["repo"] as string;
		const prNumber = node.attrs["prNumber"] as number;

		const { title, state, author, isLoading, error } = usePullRequestData(
			owner,
			repo,
			prNumber
		);

		const isNodeSelected = useIsNodeSelected();
		const editorState = useEditorState();
		const [isHoverCardOpen, setIsHoverCardOpen] = useState(false);

		// Also check if the node is within a text selection
		const pos = getPos();
		const { from, to } = editorState.selection;
		const isInTextSelection = from < pos + 1 && to > pos;

		const isSelected = isNodeSelected || isInTextSelection;

		useEffect(() => {
			// IMPORTANT: `useEffect` that updates `isSelected` is much easier to manage than
			// `open={isHoverCardOpen || isSelected}`. This way, things like the escape key will
			// reset the hover card open state, while in the other model they will stay open.
			setIsHoverCardOpen(isNodeSelected);
		}, [isNodeSelected]);

		const onClick = useEditorEventCallback((view) => {
			view.dispatch(
				view.state.tr.setSelection(NodeSelection.create(view.state.doc, getPos()))
			);
		});

		useStopEvent((_view, event) => {
			// Prevent drag events on the PR node
			if (event instanceof DragEvent) {
				event.preventDefault();
				return true;
			}
			return false;
		});

		const colors = getStateColors(state);

		return (
			<HoverCard openDelay={200} open={isHoverCardOpen} onOpenChange={setIsHoverCardOpen}>
				<HoverCardTrigger asChild>
					<span
						ref={ref}
						contentEditable={false}
						suppressContentEditableWarning
						{...props}
						className={cx(
							inlineLinkChipClassName({
								interactive: true,
								selected: isSelected,
							}),
							// IMPORTANT: When you select a range that includes a PR, don't use the
							// blue selection background for the text inside this node view. Use the
							// selection ring below instead to render selection.
							"[&::selection]:!bg-transparent",
							"[&::selection]:![color:currentColor]",
							"[&_*::selection]:!bg-transparent",
							"[&_*::selection]:![color:currentColor]"
						)}
						onClick={onClick}
					>
						<colors.Icon />
						<span className="text-sm font-normal text-primary">
							{isLoading ? (
								`${owner}/${repo}#${prNumber}`
							) : error ? (
								<>
									{owner}/{repo}
									<span className="text-secondary">#{prNumber}</span>
								</>
							) : title ? (
								title.length > 50 ? (
									<>
										<span className="text-secondary">#{prNumber}</span>{" "}
										{title.substring(0, 50)}…
									</>
								) : (
									<>
										<span className="text-secondary">#{prNumber}</span> {title}
									</>
								)
							) : (
								<>
									{owner}/{repo}
									<span className="text-secondary">#{prNumber}</span>
								</>
							)}
						</span>
					</span>
				</HoverCardTrigger>
				{/* IMPORTANT: If you don't use a portal, using keynav to select the node will
				    result in the contents of the hover card being selected, which is ugly and not
				    what we want. */}
				<HoverCardPortal>
					<HoverCardContent className="w-80">
						<div className="flex flex-col items-start gap-2 -mb-4 -mt-4 -mx-4">
							<div className="flex flex-col items-start gap-1 pt-4 px-4 pb-1">
								<div className="text-xs text-secondary truncate">
									<a
										href={`https://github.com/${owner}/${repo}`}
										className="text-secondary hover:underline hover:text-secondary"
										target="_blank"
										rel="noopener noreferrer"
									>
										{owner}/{repo}
									</a>{" "}
									<span className="text-secondary">#{prNumber}</span>
								</div>
								<div className="text-sm font-medium text-primary break-words">
									<a
										href={url}
										className="text-primary no-underline hover:text-primary hover:underline"
										target="_blank"
										rel="noopener noreferrer"
									>
										{isLoading
											? "Loading pull request…"
											: error
												? `${owner}/${repo}#${prNumber}`
												: title || `${owner}/${repo}#${prNumber}`}
									</a>
								</div>
							</div>
							<Separator />
							<div className="flex items-center gap-2 w-full px-4 pb-2">
								<div className="flex items-center gap-1">
									<div
										className={cn(
											"p-0.5 rounded",
											colors.iconColor,
											colors.bgColor
										)}
									>
										<colors.Icon />
									</div>
									<div className="text-xs capitalize">{state}</div>
								</div>
								<div className="ml-auto flex items-center gap-2 min-w-0">
									{author ? (
										<LinkButton
											className="ml-auto -mr-1"
											preset="quaternary"
											size="sm"
											href={author.htmlUrl}
											target="_blank"
											rel="noopener noreferrer"
											startIcon={
												<Image
													src={author.avatarUrl}
													alt={author.login}
													width={20}
													height={20}
													className="rounded-full border"
												/>
											}
										>
											<span className="truncate max-w-[10rem]">
												{author.login}
											</span>
										</LinkButton>
									) : (
										<LinkButton
											className="ml-auto -mr-1"
											preset="quaternary"
											size="sm"
											href={url}
											target="_blank"
											rel="noopener noreferrer"
										>
											Open on GitHub
										</LinkButton>
									)}
								</div>
							</div>
						</div>
					</HoverCardContent>
				</HoverCardPortal>
			</HoverCard>
		);
	}
);
